import { useEffect, useState } from "react";
import { globalAny, pushRecoverPasswordStateToBrowserHistory, capitalize, validateEmail, login } from "../../utils/Utils";
import { Code_OK, INVALID_PASSWORD, TEMPORARY_PASSWORD_OK } from "../../networking/networking";
import { resetCurrentPassword, resetTemporaryPassword, sendTemporaryPassword } from "../../services/loginService";
import { RecoverPasswordState, Pages, Icon } from "../../Types";
import { useNavigation } from "@react-navigation/native";
import {
	SignInScreenNavigationProp,
	LoginScreenNavigationProp,
	routeSignIn,
	SettingsNavigationProp,
	routeSettings,
	routeHome,
	routeLogin,
	routePrivacyPolicy,
} from "../../Routes";
import { PasswordReset, PasswordChange } from "../../models/PasswordReset";
import CustomizeDialogBox from "../../components/DialogMessageBox/CustomizeDialogBox";
import TextInput from "../../components/TextInput";
import ComponentThemeType from "../../models/ComponentTheme";
import TouchableButton from "../../components/TouchableButton";
import ComponentTypeEnum from "../../models/ComponentTypeEnum";
import LoadingIndicator from "../../components/LoadingIndicator";
import SplashWelcome from "../../components/SplashWelcome";
import TopAppbar from "../../components/TopAppBar";
import backIcon from "../../assets/TopNavBarIcon/icon_nav_back.svg";
import closeIcon from "../../assets/TopNavBarIcon/icon_nav_close.svg";
import Toast from "../../components/Toast";
import PrivacyPolicyButton from "../../components/PrivacyPolicyButton";
import {
	StyledButtonContainer,
	StyledButtonMainContainer,
	StyledFormContainer,
	StyledWrapper,
	StyledContainer,
	StyledButtonsContainer,
	StyledButtonsWrapper,
	StyledNarrowTextInput,
	StyledNarrowTextContainer,
} from "./styled";
import useGenericContentStore from "../../store/genericContent.store";
import NarrowScreenContainer from "../../components/Layout/NarrowScreenContainer";

const RecoverPasswordScreen = (props: any) => {
	const [recoverPasswordState, setRecoverPasswordState] = useState(RecoverPasswordState.recover);
	const navigationSignInScreen = useNavigation<SignInScreenNavigationProp>();
	const navigationLoginScreen = useNavigation<LoginScreenNavigationProp>();
	const navigation = useNavigation<any>();
	const navigationSettingsScreen = useNavigation<SettingsNavigationProp>();
	const { settings } = props.route.params;
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [tempPassword, setTempPassword] = useState("");
	const [currentPassword, setCurrentPassword] = useState("");
	const [retypePassword, setRetypePassword] = useState("");
	const [emailError, setEmailError] = useState(false);
	const [emailMessage, setEmailMessage] = useState("");
	const [modalHeader, setModalHeader] = useState("");
	const [modalBody, setModalBody] = useState("");
	const [secureTextEntryTemp, setSecureTextEntryTemp] = useState(true);
	const [secureTextEntryPass, setSecureTextEntryPass] = useState(true);
	const [secureTextEntryRepass, setSecureTextEntryRepass] = useState(true);
	const [passwordError, setPasswordError] = useState(false);
	const [passwordMessage, setPasswordMessage] = useState("");
	const [tempPasswordError, setTempPasswordError] = useState(false);
	const [tempPasswordMessage, setTempPasswordMessage] = useState("");
	const [currentPasswordError, setCurrentPasswordError] = useState(false);
	const [currentPasswordMessage, setCurrentPasswordMessage] = useState("");
	const [rePasswordError, setRePasswordError] = useState(false);
	const [rePasswordMessage, setRePasswordMessage] = useState("");
	const [loading, setLoading] = useState(false);
	const [customizeModal, setCustomizeModal] = useState(false);
	const [visible, setVisible] = useState(false);
	const [toastMessage, setToastMessage] = useState("");
	const [labelText, setLabelText] = useState("");
	const [showModal, setShowModal] = useState(false);
	const [isRecoverPassword, setIsRecoverPassword] = useState(false);
	const { isNarrow } = useGenericContentStore((state) => ({ isNarrow: state.isNarrow }));
	const onDismissSnackBar = () => setVisible(false);

	const splashWelcome = false;

	let result: any;
	let reset: any;
	let initialized: any;

	const onSubmit = async () => {
		if (!email || !onEnterEmail(email)) {
			setEmailMessage(globalAny.language.invalid_email);
			setEmailError(true);
			return;
		}

		if (recoverPasswordState === RecoverPasswordState.recover) {
			result = await sendTemporaryPassword(email);
			if (result.responseCode === Code_OK) {
				setModalHeader(globalAny.language.temporary_password_modal_header);
				setModalBody(globalAny.language.temporary_password_modal_body);
				setCustomizeModal(true);
			} else {
				setToastMessage(globalAny.language.error_temporary_password);
				setLabelText(globalAny.language.label_retry);
				setVisible(true);
			}
		}
	};

	const resetState = () => {
		setTempPassword("");
		setCurrentPassword("");
		setPassword("");
		setRetypePassword("");
		setTempPasswordError(false);
		setCurrentPasswordError(false);
		setPasswordError(false);
		setRePasswordError(false);
	};

	const onError = () => {
		setLoading(false);
		setToastMessage(globalAny.language.error_change_password);
		setLabelText(globalAny.language.label_retry);
		setVisible(true);
		resetState();
	};

	const onBackPress = () => {
		if (settings === Pages.reset) {
			navigationSettingsScreen.navigate(routeSettings, {});
			return;
		}

		switch (recoverPasswordState) {
			case RecoverPasswordState.recover:
				navigationSignInScreen.navigate(routeSignIn, {});
				setEmail("");
				break;
			case RecoverPasswordState.changePassword:
				setRecoverPasswordState(RecoverPasswordState.recover);
				pushRecoverPasswordStateToBrowserHistory(RecoverPasswordState.recover);
				break;
		}
		setPasswordError(false);
		setRePasswordError(false);
		setTempPasswordError(false);
	};

	const onClose = () => {
		if (settings === "reset") {
			//@ts-ignore
			navigation.navigate(routeHome);
		} else {
			navigationLoginScreen.navigate(routeLogin, {});
		}
	};

	const dismissCustomizeModal = () => {
		setCustomizeModal(false);
	};

	const onEnterEmail = (event: any) => {
		if (!event) {
			setEmailError(false);
			return false;
		}

		if (validateEmail(event)) {
			setEmailError(false);
			return true;
		}

		setEmailMessage(globalAny.language.invalid_email);
		setEmailError(true);
		return false;
	};

	const onEnterPassword = (event: any) => {
		setPassword(event);

		if (event.length > 0 && event !== retypePassword && retypePassword.length != 0) {
			setRePasswordMessage(globalAny.language.passwords_dont_match);
			setRePasswordError(true);
		} else {
			setRePasswordError(false);
		}

		if (validatePassword(event)) {
			setPasswordError(false);
			return;
		}

		setPasswordMessage(globalAny.language.password_rules);
		setPasswordError(true);
	};

	const onEnterTempPassword = (event: any) => {
		setTempPassword(event);
		if (event.length > 0) {
			setTempPasswordError(false);
			return;
		}

		setTempPasswordMessage(globalAny.language.invalid_temporary_password);
		setTempPasswordError(true);
	};

	const onEnterCurrentPassword = (event: any) => {
		setCurrentPassword(event);
		if (event.length < 6 && event.length !== 0) {
			setCurrentPasswordError(true);
			setCurrentPasswordMessage(globalAny.language.password_rules);
			return;
		}
		if (event.length === 0) {
			setCurrentPasswordMessage(globalAny.language.fill_up_current_password);
			setCurrentPasswordError(true);
			return;
		}

		setCurrentPasswordError(false);
	};

	const onEnterRetypePassword = (event: any) => {
		setRetypePassword(event);
		if (event.length < 6) {
			setRePasswordError(true);
			setRePasswordMessage(globalAny.language.password_rules);
			return;
		}
		if (event !== password) {
			setRePasswordMessage(globalAny.language.passwords_dont_match);
			setRePasswordError(true);
			return;
		}
		setRePasswordError(false);
	};

	const onPressEye = (event: number) => {
		if (event === 2) {
			if (secureTextEntryPass) {
				setSecureTextEntryPass(false);
				return;
			}
			setSecureTextEntryPass(true);
		} else if (event === 3) {
			if (secureTextEntryRepass) {
				setSecureTextEntryRepass(false);
				return;
			}
			setSecureTextEntryRepass(true);
		} else {
			if (secureTextEntryTemp) {
				setSecureTextEntryTemp(false);
				return;
			}
			setSecureTextEntryTemp(true);
		}
	};

	const successSent = () => {
		setCustomizeModal(false);
		setRecoverPasswordState(RecoverPasswordState.changePassword);
		pushRecoverPasswordStateToBrowserHistory(RecoverPasswordState.changePassword);
	};

	const validatePassword = (password: string) => {
		return password.length >= 6;
	};

	const returnToSettings = () => {
		setShowModal(false);
		navigationSettingsScreen.navigate(routeSettings, {});
	};

	const returnToLogin = () => {
		setShowModal(false);
		setIsRecoverPassword(false);
		navigationLoginScreen.replace(routeLogin, {});
	};

	const onChangeCurrentPassword = async () => {
		if (currentPassword.length === 0) {
			setCurrentPasswordMessage(globalAny.language.fill_up_current_password);
			setCurrentPasswordError(true);
			return;
		}

		if (password.length === 0 || passwordError) {
			setPasswordMessage(globalAny.language.password_rules);
			setPasswordError(true);
			return;
		}

		if (retypePassword.length === 0 || rePasswordError) {
			setRePasswordMessage(globalAny.language.passwords_dont_match);
			setRePasswordError(true);
			return;
		}

		if (recoverPasswordState === RecoverPasswordState.changePassword) {
			setLoading(true);

			const passwordChange: PasswordChange = {
				newPassword: password,
				confirmNewPassword: retypePassword,
				oldPassword: currentPassword,
			};

			reset = await resetCurrentPassword(passwordChange);

			if (reset.responseCode === Code_OK || reset.responseCode === TEMPORARY_PASSWORD_OK) {
				setLoading(false);
				setShowModal(true);
			} else if (reset.responseCode === INVALID_PASSWORD || currentPasswordError) {
				setToastMessage(globalAny.language.invalid_current_password);
				setLabelText(globalAny.language.label_retry);
				setLoading(false);
				setVisible(true);
			} else {
				setLoading(false);
				setToastMessage(globalAny.language.error_change_password);
				setLabelText(globalAny.language.label_retry);
				setVisible(true);
			}
		}
	};

	const onChangePassword = async () => {
		if (tempPassword.length === 0 || tempPasswordError) {
			setTempPasswordMessage(globalAny.language.invalid_temporary_password);
			setTempPasswordError(true);
			return;
		}

		if (password.length === 0 || passwordError) {
			setPasswordMessage(globalAny.language.invalid_password);
			setPasswordError(true);
			return;
		}

		if (retypePassword.length === 0 || rePasswordError) {
			setRePasswordMessage(globalAny.language.passwords_dont_match);
			setRePasswordError(true);
			return;
		}

		if (recoverPasswordState === RecoverPasswordState.changePassword) {
			setLoading(true);

			initialized = await login(email, tempPassword, () => {});

			if (initialized?.proceed) {
				const passwordReset: PasswordReset = {
					newPassword: password,
					confirmNewPassword: retypePassword,
					temporaryPasswordCode: initialized.response.temporaryPasswordCode,
				};

				reset = await resetTemporaryPassword(passwordReset);

				if (reset.responseCode === Code_OK || reset.responseCode === TEMPORARY_PASSWORD_OK) {
					setIsRecoverPassword(true);
					setLoading(false);
					setShowModal(true);
					setEmail("");
				} else {
					setIsRecoverPassword(false);
					setLoading(false);
					setToastMessage(globalAny.language.error_change_password);
					setLabelText(globalAny.language.label_retry);
					setVisible(true);
				}
			} else {
				onError();
			}
		}
	};

	const onPrivacy = () => {
		navigation.navigate(routePrivacyPolicy, {});
	};

	/**
	 * Renders the full-screen layout for email-based password recovery.
	 *
	 * This layout includes:
	 * - A text input field for entering the user's email address.
	 * - A primary button to submit the email and request a temporary password.
	 * - A privacy policy button for legal reference.
	 *
	 * The layout is optimized for full-screen devices and supports error handling,
	 * auto-focus, and accessibility features to enhance the user experience.
	 *
	 * @returns {JSX.Element} The JSX layout for the full-screen email recovery interface.
	 */
	const recoverEmailFullScreen = () => {
		return (
			<>
				{/* @ts-ignore */}
				<StyledWrapper>
					{/* @ts-ignore */}
					<StyledFormContainer>
						<TextInput
							componentTheme={ComponentThemeType.VinFast}
							label={globalAny.language.enter_email_ca}
							placeholder={globalAny.language.email_placeholder}
							onChangeText={(e) => setEmail(e)}
							value={email}
							error={emailError}
							errorMessage={emailMessage}
							autoFocus={true}
						/>
					</StyledFormContainer>
				</StyledWrapper>
				{/* @ts-ignore */}
				<StyledButtonContainer>
					{/* @ts-ignore */}
					<StyledButtonMainContainer>
						<TouchableButton componentTheme={ComponentThemeType.VinFast} type={ComponentTypeEnum.Primary} onPress={() => onSubmit()}>
							{capitalize(globalAny.language.send_temporary_password)}
						</TouchableButton>
					</StyledButtonMainContainer>
					<PrivacyPolicyButton onPress={onPrivacy} />
				</StyledButtonContainer>
			</>
		);
	};

	/**
	 * Renders the narrow screen layout for email-based password recovery.
	 *
	 * This layout includes:
	 * - A text input field for entering the user's email address.
	 * - A primary button to submit the email and request a temporary password.
	 * - A privacy policy button for legal reference.
	 *
	 * The layout is optimized for narrow viewports and supports error handling
	 * and auto-focus for improved user experience.
	 *
	 * @returns {JSX.Element} The JSX layout for the email recovery screen.
	 */
	const recoverEmailNarrowScreen = () => {
		return (
			<>
				{/* @ts-ignore */}
				<NarrowScreenContainer>
					{/* @ts-ignore */}
					<StyledButtonsContainer>
						{/* @ts-ignore */}
						<StyledButtonsWrapper>
							<TouchableButton
								componentTheme={ComponentThemeType.VinFast}
								type={ComponentTypeEnum.Primary}
								onPress={() => onSubmit()}
								leftText
							>
								{capitalize(globalAny.language.send_temporary_password)}
							</TouchableButton>
						</StyledButtonsWrapper>
						<PrivacyPolicyButton onPress={onPrivacy} />
					</StyledButtonsContainer>
					{/* @ts-ignore */}
					<StyledNarrowTextInput>
						{/* @ts-ignore */}
						<StyledNarrowTextContainer>
							<TextInput
								componentTheme={ComponentThemeType.VinFast}
								label={globalAny.language.enter_email_ca}
								placeholder={globalAny.language.email_placeholder}
								onChangeText={(e) => setEmail(e)}
								value={email}
								error={emailError}
								errorMessage={emailMessage}
								autoFocus={true}
							/>
						</StyledNarrowTextContainer>
					</StyledNarrowTextInput>
				</NarrowScreenContainer>
			</>
		);
	};

	/**
	 * Renders the full-screen layout for password recovery or reset.
	 *
	 * This layout includes:
	 * - A conditional password input field for either current or temporary password,
	 *   depending on the `settings` value (e.g., `Pages.reset`).
	 * - Input fields for the new password and retyped password.
	 * - A submit button that triggers either `onChangeCurrentPassword` or `onChangePassword`
	 *   based on the current context.
	 * - A privacy policy button for legal reference.
	 *
	 * The layout is optimized for full-screen devices and supports secure text entry toggling,
	 * error handling, and accessibility features for a smooth user experience.
	 *
	 * @returns {JSX.Element} The JSX layout for the full-screen password recovery/reset interface.
	 */
	const recoverPasswordFullScreen = () => {
		return (
			<>
				{/* @ts-ignore */}
				<StyledWrapper>
					{/* @ts-ignore */}
					<StyledFormContainer>
						<TextInput
							componentTheme={ComponentThemeType.VinFast}
							label={settings === Pages.reset ? globalAny.language.current_password : globalAny.language.temporary_password}
							onChangeText={(event) => (settings === Pages.reset ? onEnterCurrentPassword(event) : onEnterTempPassword(event))}
							onPressInputIcon={() => onPressEye(1)}
							inputIcon={secureTextEntryTemp ? Icon.eye : Icon.eyeOff}
							passwordField={secureTextEntryTemp}
							error={settings === Pages.reset ? currentPasswordError : tempPasswordError}
							errorMessage={settings === Pages.reset ? currentPasswordMessage : tempPasswordMessage}
							autoFocus={true}
						/>
						<TextInput
							componentTheme={ComponentThemeType.VinFast}
							label={globalAny.language.enter_password_ca}
							onChangeText={(event) => onEnterPassword(event)}
							onPressInputIcon={() => onPressEye(2)}
							inputIcon={secureTextEntryPass ? Icon.eye : Icon.eyeOff}
							passwordField={secureTextEntryPass}
							error={passwordError}
							errorMessage={passwordMessage}
						/>
						<TextInput
							componentTheme={ComponentThemeType.VinFast}
							label={globalAny.language.retype_new_password}
							onChangeText={(event) => onEnterRetypePassword(event)}
							onPressInputIcon={() => onPressEye(3)}
							inputIcon={secureTextEntryRepass ? Icon.eye : Icon.eyeOff}
							passwordField={secureTextEntryRepass}
							error={rePasswordError}
							errorMessage={rePasswordMessage}
						/>
					</StyledFormContainer>
				</StyledWrapper>
				{/* @ts-ignore */}
				<StyledButtonContainer>
					{/* @ts-ignore */}
					<StyledButtonMainContainer>
						<TouchableButton
							componentTheme={ComponentThemeType.VinFast}
							type={ComponentTypeEnum.Primary}
							onPress={() => (settings === Pages.reset ? onChangeCurrentPassword() : onChangePassword())}
						>
							{capitalize(globalAny.language.submit)}
						</TouchableButton>
					</StyledButtonMainContainer>
					<PrivacyPolicyButton onPress={onPrivacy} />
				</StyledButtonContainer>
			</>
		);
	};

	/**
	 * Renders the narrow screen layout for recovering or resetting a password.
	 *
	 * This UI includes:
	 * - A conditional password input field for either current or temporary password,
	 *   depending on the `settings` value.
	 * - Input fields for new password and retyped password.
	 * - A submit button that triggers either `onChangeCurrentPassword` or `onChangePassword`.
	 * - A privacy policy button for legal reference.
	 *
	 * The layout is optimized for narrow viewports and supports secure text entry toggling
	 * for each password field.
	 *
	 * @returns {JSX.Element} The JSX layout for the password recovery/reset screen.
	 */
	const recoverPasswordNarrowScreen = () => {
		return (
			<>
				{/* @ts-ignore */}
				<NarrowScreenContainer>
					{/* @ts-ignore */}
					<StyledButtonsContainer>
						{/* @ts-ignore */}
						<StyledButtonsWrapper>
							<TouchableButton
								componentTheme={ComponentThemeType.VinFast}
								type={ComponentTypeEnum.Primary}
								onPress={() => (settings === Pages.reset ? onChangeCurrentPassword() : onChangePassword())}
								leftText
							>
								{capitalize(globalAny.language.submit)}
							</TouchableButton>
						</StyledButtonsWrapper>
						<PrivacyPolicyButton onPress={onPrivacy} />
					</StyledButtonsContainer>
					{/* @ts-ignore */}
					<StyledNarrowTextInput>
						{/* @ts-ignore */}
						<StyledNarrowTextContainer>
							<TextInput
								componentTheme={ComponentThemeType.VinFast}
								label={settings === Pages.reset ? globalAny.language.current_password : globalAny.language.temporary_password}
								onChangeText={(event) => (settings === Pages.reset ? onEnterCurrentPassword(event) : onEnterTempPassword(event))}
								onPressInputIcon={() => onPressEye(1)}
								inputIcon={secureTextEntryTemp ? Icon.eye : Icon.eyeOff}
								passwordField={secureTextEntryTemp}
								error={settings === Pages.reset ? currentPasswordError : tempPasswordError}
								errorMessage={settings === Pages.reset ? currentPasswordMessage : tempPasswordMessage}
								autoFocus={true}
							/>

							<TextInput
								componentTheme={ComponentThemeType.VinFast}
								label={globalAny.language.enter_password_ca}
								onChangeText={(event) => onEnterPassword(event)}
								onPressInputIcon={() => onPressEye(2)}
								inputIcon={secureTextEntryPass ? Icon.eye : Icon.eyeOff}
								passwordField={secureTextEntryPass}
								error={passwordError}
								errorMessage={passwordMessage}
							/>
							<TextInput
								componentTheme={ComponentThemeType.VinFast}
								label={globalAny.language.retype_new_password}
								onChangeText={(event) => onEnterRetypePassword(event)}
								onPressInputIcon={() => onPressEye(3)}
								inputIcon={secureTextEntryRepass ? Icon.eye : Icon.eyeOff}
								passwordField={secureTextEntryRepass}
								error={rePasswordError}
								errorMessage={rePasswordMessage}
							/>
						</StyledNarrowTextContainer>
					</StyledNarrowTextInput>
				</NarrowScreenContainer>
			</>
		);
	};

	/**
	 * Sets the initial state for password recovery based on the `settings` value.
	 * If the component is unmounted, it prevents further state updates.
	 */
	useEffect(() => {
		let unmounted = false;
		if (!unmounted) {
			if (settings === Pages.reset) {
				setRecoverPasswordState(RecoverPasswordState.changePassword);
				return;
			}
			setRecoverPasswordState(RecoverPasswordState.recover);
		}

		return () => {
			unmounted = true;
		};
	}, []);

	if (loading) {
		return <LoadingIndicator show={loading} showSpinner={loading}></LoadingIndicator>;
	}

	if (splashWelcome) {
		return <SplashWelcome show={splashWelcome} name={"User"} isRecoverPassword={isRecoverPassword}></SplashWelcome>;
	}

	return (
		<>
			{/* @ts-ignore */}
			<StyledContainer>
				<TopAppbar
					leftIcon={closeIcon}
					leftIconWidth={92}
					leftTitleIcon={backIcon}
					screenTitle={
						settings === Pages.reset ? capitalize(globalAny.language.change_password) : capitalize(globalAny.language.recover_password)
					}
					onPressLeftTitleIcon={onBackPress}
					onPressLeftIcon={onClose}
					isSingleLogo={true}
					isNarrow={isNarrow}
				/>

				{recoverPasswordState === RecoverPasswordState.recover && (isNarrow ? recoverEmailNarrowScreen() : recoverEmailFullScreen())}

				{recoverPasswordState === RecoverPasswordState.changePassword &&
					(isNarrow ? recoverPasswordNarrowScreen() : recoverPasswordFullScreen())}

				<Toast visible={visible} text={toastMessage} label={labelText} onDismissSnackBar={onDismissSnackBar} />
			</StyledContainer>
			{customizeModal ? (
				<CustomizeDialogBox
					Header={modalHeader}
					Body={modalBody}
					ButtonTextRight={
						settings === Pages.reset ? capitalize(globalAny.language.reset_password) : capitalize(globalAny.language.recover_password)
					}
					SingleButton={true}
					CloseButton={false}
					Width={680}
					Height={"auto"}
					TypeButtonColorRight={ComponentTypeEnum.Primary}
					onDismiss={dismissCustomizeModal}
					onPressRight={successSent}
				/>
			) : (
				<></>
			)}

			{showModal ? (
				<CustomizeDialogBox
					Header={globalAny.language.success_change_pass}
					Body={globalAny.language.change_pass_modal}
					ButtonTextRight={settings === Pages.reset ? globalAny.language.return_to_settings : globalAny.language.ok}
					SingleButton={true}
					CloseButton={false}
					Width={680}
					Height={"auto"}
					TypeButtonColorRight={ComponentTypeEnum.Primary}
					onPressRight={settings === Pages.reset ? returnToSettings : returnToLogin}
				/>
			) : (
				<></>
			)}
		</>
	);
};
export default RecoverPasswordScreen;
